import React, { useEffect, useRef, useState } from 'react';

import { ElementStateWrapper, focusOutlineColors, transitionAnimation } from 'lib/core';
import { IconArrowDropDown } from 'lib/icons';
import { Menu, MenuListItem } from 'lib/menu';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { PageHeaderTitle } from '../elements/PageHeaderTitle';

const StyledTitleWithDropdownContainer = styled.div`
  cursor: pointer;
  display: flex;
  align-content: flex-start;
  flex-direction: row;
  gap: 0;
  width: fit-content;
  padding-right: 8px;
`;

const StyledIconArrowDropDown = styled(IconArrowDropDown)`
  ${transitionAnimation('transform')};
  align-self: center;
  margin-left: -16px;
  min-width: 24px;
  ${({ $isOpen }) =>
    $isOpen &&
    css`
      transform: rotate(180deg);
    `};
`;

const StyledMenu = styled(Menu)`
  margin-left: 12px;
`;

const PageHeaderTitleDropdownBlock = ({
  dataTestId = undefined,
  onClose = () => {},
  onOpen = () => {},
  onSelect,
  options,
  selectedOption,
  ...other
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedTitle, setSelectedTitle] = useState(selectedOption.label);
  const titleRef = useRef();
  const menuContainerRef = useRef();

  const handleOpen = () => onOpen();

  const handleClose = () => onClose();

  const handleSelect = (option) => {
    onSelect(option);
    setSelectedTitle(option.label);
    handleClose();
  };

  const titleStateStyles = {
    active: [],
    default: css`
      border-radius: 8px;
      ${transitionAnimation('box-shadow')}
    `,
    disabled: [],
    focus: css`
      position: fixed;
      box-shadow: 0 0 0 2px ${({ theme }) => theme.color.primary[500].value},
        0 0 0 5px ${({ theme }) => focusOutlineColors.getPrimary(theme)};
      border-radius: 8px;
      ${transitionAnimation('box-shadow')}
    `,
    hover: [],
  };

  // watch for any external change of selectedOption
  useEffect(() => {
    setSelectedTitle(selectedOption.label);
  }, [selectedOption]);

  useEffect(() => {
    const setIsOpenFromMenu = (event) => {
      if (event.detail.ref === menuContainerRef) {
        setMenuOpen(event.detail.openMenu);
      }
    };
    document.addEventListener('customMenuOpenEvent', setIsOpenFromMenu);

    return () => document.removeEventListener('customMenuOpenEvent', setIsOpenFromMenu);
  }, []);

  return (
    <>
      <ElementStateWrapper stateStyles={titleStateStyles}>
        <StyledTitleWithDropdownContainer
          data-testid={dataTestId || undefined}
          onClick={menuOpen ? null : handleOpen}
          ref={titleRef}
        >
          <PageHeaderTitle data-testid={dataTestId ? `${dataTestId}-title` : undefined}>
            {selectedTitle}
          </PageHeaderTitle>
          <StyledIconArrowDropDown
            data-testid={dataTestId ? `${dataTestId}-arrow-icon` : undefined}
            $isOpen={menuOpen}
          />
        </StyledTitleWithDropdownContainer>
      </ElementStateWrapper>
      <StyledMenu
        data-testid={dataTestId ? `${dataTestId}-menu` : undefined}
        isAuto={false}
        isOpen={false}
        labelRef={titleRef}
        placement="bottom-start"
        ref={menuContainerRef}
        triggerOffset={-8}
        {...other}
      >
        {options.map((option, ind) => (
          <MenuListItem
            dataTestId={dataTestId ? `${dataTestId}-menu-item-${ind}` : undefined}
            graphicContent={option.icon}
            hideSelectedItemCheckmark
            isDisabled={option.isDisabled}
            isSelected={selectedTitle === option.label}
            key={ind}
            onClick={() => handleSelect(option)}
          >
            {option.label}
          </MenuListItem>
        ))}
      </StyledMenu>
    </>
  );
};

PageHeaderTitleDropdownBlock.propTypes = {
  /** Id value used for testing */
  dataTestId: PropTypes.string,
  /** Callback that is called when Page Header menu closes */
  onClose: PropTypes.func,
  /** Callback that is called when Page Header menu opens */
  onOpen: PropTypes.func,
  /** Callback called on option click or keypress, forwards selected option, setIsOpen callback  */
  onSelect: PropTypes.func.isRequired,
  /** List of options to select from */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
    }).isRequired
  ).isRequired,
  /** Current selected option */
  selectedOption: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.node.isRequired,
  }).isRequired,
};

export { PageHeaderTitleDropdownBlock };
